import { useCallback, useMemo, useRef, useState } from "react";
import { EditQuizStep } from "../../proctoring-options";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { ProctoringDataType } from "./useSettingsState";

type UseNavStateProps = Pick<
	UseEditQuizStateProps,
	| "step"
	| "setStep"
	| "forceStartOnFirstPage"
	| "initialProctoringAssessmentConfiguration"
	| "proctoringAssessmentConfiguration"
>;

/**Anything related to back/next/stepper */
export default function useNavState({
	step,
	setStep,
	forceStartOnFirstPage,
	initialProctoringAssessmentConfiguration,
	proctoringAssessmentConfiguration,
}: UseNavStateProps) {
	const [curPage, setCurPage] = useState<number>(0);
	const [completedStepsSet, setCompletedStepsSet] = useState<Set<EditQuizStep>>(new Set()); // Using page name instead of index because of dynamic steps (aka the live session step makes the steps dynamic)

	/**
	 *  Forces the user to stay on a specific page name
	 *
	 * Prevents race conditions where async data (like new categories) shifts page indexes.
	 */
	const [stickyPageName, setStickyPageName] = useState<EditQuizStep | undefined>(undefined);
	const [initialNavigationDone, setInitialNavigationDone] = useState(false);

	const curPageValue = step ?? curPage;
	const setCurPageValue = setStep ?? setCurPage;

	const modalBodyRef = useRef<HTMLDivElement>(null);

	// The browser's own implicit scroll-into-view on focus is fine when modalBody already fits the
	// viewport, but inside a host shorter than the wizard's content (e.g. an LMS's own injected
	// settings panel) it can leave the step's heading, description and controls above the fold.
	// Prevent that and align the step's own top explicitly instead: scrollTop resets modalBody's
	// own overflow (its scrollport never moves on its own scrollIntoView call), and scrollIntoView
	// covers every scrollable ancestor above it — between the two, whichever layer actually ends up
	// scrolling is reset regardless.
	const focusStep = useCallback(() => {
		const el = modalBodyRef.current;
		if (!el) return;
		el.focus({ preventScroll: true });
		el.scrollTop = 0;
		el.scrollIntoView({ block: "start", inline: "nearest" });
	}, []);

	const shouldStartOnLastStep = useMemo(() => {
		return (
			Boolean(forceStartOnFirstPage) === false &&
			Boolean(initialProctoringAssessmentConfiguration?.enabled) &&
			!initialNavigationDone
		);
	}, [forceStartOnFirstPage, initialNavigationDone, initialProctoringAssessmentConfiguration?.enabled]);

	const shouldStartOnThirdPartyStep = useCallback(
		(thirdPartyPlatforms: ProctoringDataType["thirdPartyPlatforms"]) => {
			if (initialNavigationDone) return false;
			if (forceStartOnFirstPage) return true;
			if (
				!proctoringAssessmentConfiguration ||
				!proctoringAssessmentConfiguration.thirdPartyAssessmentOption ||
				thirdPartyPlatforms.length < 1
			) {
				return false;
			}

			const platformId = proctoringAssessmentConfiguration.thirdPartyAssessmentOption.thirdPartyPlatformId;
			const isPlatformAllowed = thirdPartyPlatforms.some((platform) => platform.platformId === platformId);

			return !platformId || !isPlatformAllowed;
		},
		[forceStartOnFirstPage, initialNavigationDone, proctoringAssessmentConfiguration],
	);

	const handleInitialNavigation = useCallback((initialStep: EditQuizStep) => {
		setStickyPageName(initialStep);
		setInitialNavigationDone(true);
	}, []);

	const resetStickyPage = useCallback(() => setStickyPageName(undefined), []);

	const markStepAsCompleted = useCallback((stepName: EditQuizStep) => {
		setCompletedStepsSet((prev) => {
			if (prev.has(stepName)) return prev;
			const next = new Set(prev);
			next.add(stepName);
			return next;
		});
	}, []);

	const unmarkStepAsCompleted = useCallback((stepName: EditQuizStep) => {
		setCompletedStepsSet((prev) => {
			if (!prev.has(stepName)) return prev;
			const next = new Set(prev);
			next.delete(stepName);
			return next;
		});
	}, []);

	const markMultipleStepsCompleted = useCallback((steps: EditQuizStep[]) => {
		setCompletedStepsSet((prev) => {
			const next = new Set(prev);
			steps.forEach((s) => next.add(s));
			return next;
		});
	}, []);

	return {
		state: {
			curPageValue,
			completedStepsSet,
			shouldStartOnLastStep,
			modalBodyRef,
			stickyPageName,
		},
		action: {
			focusStep,
			markMultipleStepsCompleted,
			markStepAsCompleted,
			unmarkStepAsCompleted,
			handleInitialNavigation,
			resetStickyPage,
			shouldStartOnThirdPartyStep,
		},
		internal: {
			/**USE handleGoToStep -> it has validation */
			setCurPageValue,
		},
	};
}
